import { forwardRef } from 'react';

import { cn } from './cn.js';

import type { ButtonHTMLAttributes, ReactNode } from 'react';

type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';
type ButtonSize = 'sm' | 'md' | 'lg';

interface GlassButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  children: ReactNode;
}

const variantClasses: Record<ButtonVariant, string> = {
  primary:
    'bg-primary text-primary-fg hover:bg-primary-hover shadow-[var(--glass-shadow)] hover:shadow-[var(--glass-glow-hover)] active:shadow-none',
  secondary: 'glass hover:bg-[var(--glass-bg-hover)] text-text active:bg-[var(--glass-bg-strong)]',
  ghost:
    'text-text-secondary hover:bg-[var(--glass-bg)] hover:text-text active:bg-[var(--glass-bg-strong)]',
  danger: 'bg-danger text-white hover:bg-danger/90 shadow-[var(--glass-shadow)] active:shadow-none',
};

const sizeClasses: Record<ButtonSize, string> = {
  sm: 'h-7 px-2.5 text-xs gap-1',
  md: 'h-8.5 px-3.5 text-sm gap-1.5',
  lg: 'h-10 px-5 text-sm gap-2',
};

export const GlassButton = forwardRef<HTMLButtonElement, GlassButtonProps>(
  ({ variant = 'primary', size = 'md', className, children, ...props }, ref) => {
    return (
      <button
        ref={ref}
        className={cn(
          'inline-flex items-center justify-center font-medium rounded-lg',
          'transition-all duration-[var(--dur)] ease-[var(--ease)]',
          'focus-visible:outline-2 focus-visible:outline-primary focus-visible:outline-offset-2',
          'disabled:opacity-50 disabled:pointer-events-none',
          'cursor-pointer select-none whitespace-nowrap',
          'hover:translate-y-[-1px] active:translate-y-0',
          variantClasses[variant],
          sizeClasses[size],
          className,
        )}
        {...props}
      >
        {children}
      </button>
    );
  },
);
